import styled from 'styled-components'

export const StyledDetailPanel = styled.div<{
  $isOpen: boolean
  $width: string
  $isResizing: boolean
  $contained?: boolean
  $disableResize?: boolean
}>`
  position: ${({ $contained }) => ($contained ? 'absolute' : 'fixed')};
  top: ${({ $contained }) => ($contained ? '0' : '48px')};
  right: 0;
  width: ${({ $width, $disableResize }) => ($disableResize ? '100%' : $width)};
  ${({ $contained, $disableResize }) =>
    $disableResize
      ? 'bottom: 0; left: 0; max-width: 100%;'
      : $contained
        ? 'bottom: 0; max-width: 90%;'
        : 'height: calc(100dvh - 48px);'}
  background: ${({ theme }) => theme.colors.background.primary};
  box-shadow: ${({ $isOpen, theme }) => ($isOpen ? theme.shadows.xl : 'none')};
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: ${({ $isResizing }) =>
    $isResizing
      ? 'none'
      : 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.5s cubic-bezier(0.4, 0, 0.2, 1)'};
  z-index: ${({ $contained, theme }) =>
    $contained ? '1000' : theme.zIndex.detailPanel};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    max-width: 100%;
  }
`

export const StyledDetailPanelHeader = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.medium};
  }
`

export const StyledDetailPanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  position: relative;
  max-width: 100%;
  min-width: 0;

  /* Ensure all child elements respect container width */
  & > * {
    max-width: 100%;
    min-width: 0;
  }
`

export const StyledDetailPanelFooter = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  padding: ${({ theme }) => theme.spacing.medium}
    ${({ theme }) => theme.spacing.large};
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.small};
  flex-shrink: 0;
  flex-wrap: wrap;

  /* Buttons should adapt to container width */
  & > button {
    flex: 1 1 auto;
    min-width: fit-content;
    max-width: 200px;
    font-size: ${({ theme }) => theme.typography.fontSize.small};
    padding: ${({ theme }) => theme.spacing.small}
      ${({ theme }) => theme.spacing.medium};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.xsmall}
      ${({ theme }) => theme.spacing.small};
    padding-bottom: calc(
      ${({ theme }) => theme.spacing.xsmall} + env(safe-area-inset-bottom, 0px)
    );
    gap: ${({ theme }) => theme.spacing.xsmall};

    & > button {
      flex: 1 1 45%;
      min-width: 80px;
      max-width: none;
      font-size: 12px;
      padding: 6px 10px;
    }
  }
`

export const CloseButton = styled.button`
  width: 26px;
  height: 26px;
  border: none;
  border-right: 1px solid #e5e5e5;
  border-radius: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b6b6b;
  transition: all 0.2s;
  padding-right: 6px;
  margin-right: 2px;
  flex-shrink: 0;

  &:hover {
    color: #1a1a1a;
  }
`

export const ResizeHandle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 10px;
  height: 100%;
  cursor: col-resize;
  background: transparent;
  transition: background-color 0.2s ease;
  z-index: 1001;
  display: block;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}20;
  }

  &:active {
    background: ${({ theme }) => theme.colors.primary}40;
  }

  &::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 64px;
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: 2px;
    opacity: 0.6;
    transition: all 0.2s ease;
  }

  &:hover::before {
    opacity: 1;
    background: ${({ theme }) => theme.colors.primary};
    height: 96px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`
